import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Instagram } from "lucide-react";
import { LogoMark } from "@/components/ui-kit";
import SupportWidget from "@/components/SupportWidget";

const NAV = [
  { to: "/features/student-deals", label: "Deals" },
  { to: "/features/jobs", label: "Jobs" },
  { to: "/features/events", label: "Student Events" },
  { to: "/faqs", label: "Support" },
];

const FOOTER_NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/membership", label: "Membership" },
  { to: "/features", label: "Features" },
  { to: "/resources", label: "Resources" },
  { to: "/faqs", label: "FAQs" },
];

export const MkCTA = ({ children = "Become a Free Member", className = "", testid, onClick }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={onClick || (() => navigate("/onboarding"))}
      className={`rounded-full bg-[#0A1633] text-white font-medium px-8 py-4 text-sm sm:text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(10,22,51,0.18)] focus:ring-2 focus:ring-[#D4AF37] focus:outline-none ${className}`}
    >
      {children}
    </button>
  );
};

const MkHeading = ({ as: Tag = "h2", children, className = "" }) => (
  <Tag className={`font-bold tracking-tight text-[#0A1633] ${className}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
    {children}
  </Tag>
);

export { MkHeading };

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFDFC] text-slate-900" data-testid="marketing-layout" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100" data-testid="marketing-header">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between py-4">
          <Link to="/" data-testid="marketing-logo" className="flex items-center gap-2.5 group">
            <LogoMark className="w-10 h-10" />
            <span className="font-bold text-[#0A1633] text-lg tracking-tight leading-none">
              Castles <span className="text-[#D4AF37]">Creation</span>
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-testid="marketing-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-300 ${isActive ? "text-[#0A1633] border-b-2 border-[#D4AF37] pb-1" : "text-slate-500 hover:text-[#0A1633]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-500 transition-colors duration-300 hover:text-[#0A1633]">
              Login
            </Link>
          </div>
          <button data-testid="marketing-mobile-menu-button" className="lg:hidden text-[#0A1633] p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-100 px-6 py-6 space-y-1" data-testid="marketing-mobile-menu">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block py-2.5 text-base font-medium ${isActive ? "text-[#0A1633]" : "text-slate-500"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/login" data-testid="mobile-nav-login" onClick={() => setOpen(false)} className="block py-2.5 text-base font-medium text-slate-500">Login</Link>
          </div>
        )}
      </header>

      <Outlet />

      <footer className="bg-[#0A1633] text-slate-300" data-testid="marketing-footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5">
                <LogoMark className="w-10 h-10" />
                <span className="font-bold text-white text-lg">Castles <span className="text-[#D4AF37]">Creation</span></span>
              </div>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
                The student success platform built by former international students. Settle faster, save money and make the most of student life in the UK.
              </p>
              <a
                href="https://instagram.com/castlescreation"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="mt-5 inline-flex items-center gap-2.5 text-sm text-slate-400 transition-colors duration-300 hover:text-[#D4AF37]"
              >
                <span className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center transition-colors duration-300 hover:border-[#D4AF37]">
                  <Instagram className="w-4 h-4" strokeWidth={1.8} />
                </span>
                @castlescreation
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Explore</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                {FOOTER_NAV.map((n) => (
                  <li key={n.to}><Link to={n.to} className="transition-colors duration-300 hover:text-white">{n.label}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">Members</p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li><Link to="/login" data-testid="footer-login" className="transition-colors duration-300 hover:text-white">Login</Link></li>
                <li><Link to="/onboarding" data-testid="footer-join" className="transition-colors duration-300 hover:text-white flex items-center gap-1.5">Become a Member <ArrowRight className="w-3.5 h-3.5" /></Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
            <p>Castles Creation. Built by students, for students.</p>
            <p>Free membership. Always.</p>
          </div>
        </div>
      </footer>

      <SupportWidget />
    </div>
  );
}
